import type { APIError, ErrorCode, ResolveResponse, YouTubeSubtitleManifest, YouTubeSubtitleTrack } from "./types";

export class ResolveError extends Error {
  code: ErrorCode;
  requestId?: string;

  constructor(code: ErrorCode, requestId?: string) {
    super(code);
    this.name = "ResolveError";
    this.code = code;
    this.requestId = requestId;
  }
}

export type ResolveOptions = {
  cursor?: string;
  profilePreview?: boolean;
};

export async function resolvePost(url: string, signal?: AbortSignal, options: ResolveOptions = {}): Promise<ResolveResponse> {
  let response: Response;
  try {
    const body: { url: string; cursor?: string; profilePreview?: boolean } = { url };
    if (options.cursor) body.cursor = options.cursor;
    if (options.profilePreview) body.profilePreview = true;
    response = await fetch("/api/v1/resolve", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal
    });
  } catch {
    throw new ResolveError("NETWORK_ERROR");
  }
  const payload = (await response.json().catch(() => null)) as ResolveResponse | APIError | null;
  if (!response.ok || !payload || "error" in payload) {
    const error = payload && "error" in payload ? payload.error : undefined;
    throw new ResolveError(error?.code || "NETWORK_ERROR", error?.requestId);
  }
  return payload;
}

export async function loadYouTubeSubtitles(manifestURL: string, signal?: AbortSignal): Promise<YouTubeSubtitleManifest> {
  if (!manifestURL.startsWith("/api/v1/youtube/subtitles?token=")) {
    throw new ResolveError("PARSE_CHANGED");
  }
  let response: Response;
  try {
    response = await fetch(manifestURL, { signal, headers: { Accept: "application/json" } });
  } catch {
    throw new ResolveError("NETWORK_ERROR");
  }
  const payload = (await response.json().catch(() => null)) as YouTubeSubtitleManifest | APIError | null;
  if (!response.ok || !payload || "error" in payload) {
    const error = payload && "error" in payload ? payload.error : undefined;
    throw new ResolveError(error?.code || "NETWORK_ERROR", error?.requestId);
  }
  if (!isSafeYouTubeSubtitleManifest(payload)) {
    throw new ResolveError("PARSE_CHANGED");
  }
  return payload;
}

function isSafeYouTubeSubtitleManifest(payload: YouTubeSubtitleManifest): boolean {
  return Array.isArray(payload.tracks) && payload.tracks.length <= 300 && typeof payload.expiresAt === "string" &&
    payload.tracks.every(isSafeYouTubeSubtitleTrack);
}

function isSafeYouTubeSubtitleTrack(track: YouTubeSubtitleTrack): boolean {
  if (!track || !/^[A-Za-z0-9_-]{1,32}$/.test(track.code) || typeof track.name !== "string" ||
      track.name.length < 1 || track.name.length > 128 || typeof track.autoGenerated !== "boolean" || !track.downloads) {
    return false;
  }
  const downloads = Object.entries(track.downloads);
  return downloads.length > 0 && downloads.every(([format, url]) =>
    (format === "srt" || format === "vtt") && typeof url === "string" && url.startsWith("/api/v1/youtube/subtitle?token="));
}
